import { yupResolver } from '@hookform/resolvers/yup';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { Account, BeneficiaryType, EdbCategory, RMDCalculationResult } from '@ndvr/portfolio-api';

import { FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { convertFormDateToServerDate, convertServerDateToFormDate } from 'utils/date-formatter';
import { useAccount } from 'views/portfolio/account-queries';

import { useUpdateInheritedRmdProperties } from './rmd-queries';

interface RmdBeneficiaryModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    account: RMDCalculationResult;
}

// Backend uses 2020-01-01 as the SECURE Act cutoff
const SECURE_ACT_DATE = '2020-01-01';

const TODAY = new Date().toISOString().slice(0, 10);

const formSchema = Yup.object({
    decedentDateOfDeath: Yup.string()
        .required('Date of death is required')
        .test('not-future', 'Date of death cannot be in the future', (v) => !v || v <= TODAY),
    decedentDateOfBirth: Yup.string()
        .required('Decedent date of birth is required')
        .test('not-future', 'Date of birth cannot be in the future', (v) => !v || v <= TODAY)
        .when('decedentDateOfDeath', {
            is: (dod: string) => !!dod,
            then: (s) =>
                s.test('dob-before-dod', 'Date of birth must be before date of death', function (dob) {
                    const dod = this.parent.decedentDateOfDeath as string;
                    if (!dob || !dod) return true;
                    return dob < dod;
                }),
        }),
    beneficiaryType: Yup.string().required('Beneficiary type is required'),
    edbCategory: Yup.string()
        .default('')
        .when('beneficiaryType', {
            is: BeneficiaryType.EDB,
            then: (s) => s.required('EDB Category is required when Beneficiary Type is EDB'),
            otherwise: (s) => s.optional(),
        }),
}).test('beneficiary-type-secure-act', '', function (values) {
    const { decedentDateOfDeath, beneficiaryType } = values;
    if (!decedentDateOfDeath || !beneficiaryType) return true;

    const isPreSecureAct = decedentDateOfDeath < SECURE_ACT_DATE;
    const preSecureTypes = [BeneficiaryType.SPOUSE, BeneficiaryType.NON_SPOUSE] as string[];
    const postSecureTypes = [BeneficiaryType.EDB, BeneficiaryType.NON_EDB] as string[];

    if (isPreSecureAct && postSecureTypes.includes(beneficiaryType)) {
        return this.createError({
            path: 'beneficiaryType',
            message: 'Use Spouse / Non-Spouse for decedents who died before the SECURE Act (2020)',
        });
    }
    if (!isPreSecureAct && preSecureTypes.includes(beneficiaryType)) {
        return this.createError({
            path: 'beneficiaryType',
            message: 'Use EDB / Non-EDB for decedents who died on or after the SECURE Act (2020)',
        });
    }
    return true;
});

type FormValues = Yup.InferType<typeof formSchema>;

const EDB_CATEGORIES: { value: EdbCategory; label: string }[] = [
    { value: EdbCategory.SURVIVING_SPOUSE, label: 'Surviving Spouse' },
    { value: EdbCategory.MINOR_CHILD, label: 'Minor Child' },
    { value: EdbCategory.DISABLED, label: 'Disabled' },
    { value: EdbCategory.CHRONICALLY_ILL, label: 'Chronically Ill' },
    { value: EdbCategory.NOT_MORE_THAN_10_YEARS_YOUNGER, label: 'Within 10 Years of Age' },
];

export const RmdBeneficiaryModal: React.FC<RmdBeneficiaryModalProps> = ({ open, onOpenChange, account }) => {
    const { data: accountDetail, isLoading, isError } = useAccount(open ? account.accountGUID : undefined);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>Beneficiary & Decedent Info</DialogTitle>
                    <DialogDescription>Configure inherited IRA details for account {account.pId}</DialogDescription>
                </DialogHeader>

                {isError ? (
                    <div className="py-6 text-center text-sm text-red-500">Failed to load account details.</div>
                ) : isLoading || !accountDetail ? (
                    <div className="py-6 text-center text-sm text-gray-400">Loading…</div>
                ) : (
                    <RmdBeneficiaryForm account={account} accountDetail={accountDetail} onOpenChange={onOpenChange} />
                )}
            </DialogContent>
        </Dialog>
    );
};

interface RmdBeneficiaryFormProps {
    account: RMDCalculationResult;
    accountDetail: Account;
    onOpenChange: (open: boolean) => void;
}

const RmdBeneficiaryForm: React.FC<RmdBeneficiaryFormProps> = ({ account, accountDetail, onOpenChange }) => {
    const { mutate: updateInheritedRmd, isPending } = useUpdateInheritedRmdProperties();

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        mode: 'onSubmit',
        defaultValues: {
            decedentDateOfDeath: accountDetail.decedentDateOfDeath
                ? convertServerDateToFormDate(accountDetail.decedentDateOfDeath)
                : '',
            decedentDateOfBirth: accountDetail.decedentDateOfBirth
                ? convertServerDateToFormDate(accountDetail.decedentDateOfBirth)
                : '',
            beneficiaryType: accountDetail.beneficiaryType ?? '',
            edbCategory: accountDetail.edbCategory ?? '',
        },
    });

    const dateOfDeath = form.watch('decedentDateOfDeath');
    const beneficiaryType = form.watch('beneficiaryType');

    const isPreSecureAct = dateOfDeath ? dateOfDeath < SECURE_ACT_DATE : null;

    const beneficiaryOptions = useMemo(
        () =>
            isPreSecureAct === null
                ? []
                : isPreSecureAct
                  ? [
                        { value: BeneficiaryType.SPOUSE, label: 'Spouse' },
                        { value: BeneficiaryType.NON_SPOUSE, label: 'Non-Spouse' },
                    ]
                  : [
                        { value: BeneficiaryType.EDB, label: 'Eligible Designated Beneficiary (EDB)' },
                        { value: BeneficiaryType.NON_EDB, label: 'Non-Eligible Designated Beneficiary' },
                    ],
        [isPreSecureAct],
    );

    const showEdbCategory = beneficiaryType === (BeneficiaryType.EDB as string);

    const handleDateOfDeathChange = () => {
        form.setValue('beneficiaryType', '');
        form.setValue('edbCategory', '');
    };

    const handleSave = (values: FormValues) => {
        updateInheritedRmd(
            {
                accountGUID: account.accountGUID,
                request: {
                    decedentDateOfDeath: values.decedentDateOfDeath
                        ? convertFormDateToServerDate(values.decedentDateOfDeath)
                        : undefined,
                    decedentDateOfBirth: values.decedentDateOfBirth
                        ? convertFormDateToServerDate(values.decedentDateOfBirth)
                        : undefined,
                    beneficiaryType: (values.beneficiaryType as BeneficiaryType) || undefined,
                    edbCategory:
                        values.beneficiaryType === (BeneficiaryType.EDB as string)
                            ? (values.edbCategory as EdbCategory) || undefined
                            : undefined,
                },
            },
            { onSuccess: () => onOpenChange(false) },
        );
    };

    return (
        <>
            <Form {...form}>
                <form id="beneficiary-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-5 py-2">
                    {/* Decedent Section */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900">Decedent</h4>
                        <FormField
                            control={form.control}
                            name="decedentDateOfDeath"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date of Death</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="date"
                                            {...field}
                                            onChange={(e) => {
                                                const prev = field.value;
                                                const next = e.target.value;
                                                field.onChange(e);
                                                const prevIsPreSecureAct = prev ? prev < SECURE_ACT_DATE : null;
                                                const nextIsPreSecureAct = next ? next < SECURE_ACT_DATE : null;
                                                if (prevIsPreSecureAct !== nextIsPreSecureAct) {
                                                    handleDateOfDeathChange();
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="decedentDateOfBirth"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Decedent Date of Birth</FormLabel>
                                    <FormControl>
                                        <Input type="date" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <Separator />

                    {/* Beneficiary Section */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900">Beneficiary</h4>
                        <FormSelect
                            control={form.control}
                            name="beneficiaryType"
                            label="Beneficiary Type"
                            placeholder={isPreSecureAct === null ? 'Enter date of death first' : 'Select type'}
                            disabled={isPreSecureAct === null}
                        >
                            {beneficiaryOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </FormSelect>

                        {showEdbCategory && (
                            <FormSelect
                                control={form.control}
                                name="edbCategory"
                                label="EDB Category"
                                placeholder="Select category"
                            >
                                {EDB_CATEGORIES.map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </SelectItem>
                                ))}
                            </FormSelect>
                        )}

                        <div className="space-y-1.5">
                            <p className="text-sm font-medium text-gray-700">Beneficiary Date of Birth</p>
                            <p className="text-sm text-gray-400">Sourced from client profile</p>
                        </div>
                    </div>
                </form>
            </Form>

            <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button type="submit" form="beneficiary-form" disabled={isPending}>
                    {isPending ? 'Saving…' : 'Save'}
                </Button>
            </DialogFooter>
        </>
    );
};
